// src/components/InputForm.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  Step,
  StepDescription,
  StepIndicator,
  StepTitle,
  StepSeparator,
  Stepper,
  useToast,
  Flex,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { 
  ChevronRight, 
  ChevronLeft,
  UserCircle2,
  CircleDollarSign,
  Palette,
  List
} from 'lucide-react';
import axios from 'axios';

// ステップコンポーネントのインポート
import GenderStep from './steps/GenderStep';
import BudgetStep from './steps/BudgetStep';
import ColorStep from './steps/ColorStep';
import FeaturesStep from './steps/FeaturesStep';
import MatchResults from './MatchResults';

const steps = [
  {
    title: '性別',
    description: 'お子様の性別',
    icon: UserCircle2
  },
  {
    title: '価格帯',
    description: 'ご希望の予算',
    icon: CircleDollarSign
  },
  {
    title: 'カラー',
    description: 'お好みの色',
    icon: Palette
  },
  {
    title: '特徴',
    description: '重視したい機能',
    icon: List
  }
];

const InputForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [matchResults, setMatchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  // リセット関数を追加
  const handleReset = () => {
    setActiveStep(0);
    setFormData({});
    setMatchResults(null);
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // 各ステップの入力検証
  const stepValidation = {
    0: formData.gender,
    1: formData.price,
    2: formData.colors?.length > 0,
    3: formData.features?.length > 0
  };

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      setIsLoading(true);
      try {
        const matchingData = {
          gender: formData.gender,
          colors: formData.colors || [],
          price: formData.price,
          features: formData.features || []
        };
  
        console.log('送信するマッチングデータ:', matchingData);
  
        // URLを/api/randoseru/matchに変更
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const response = await axios.post(`${API_URL}/api/randoseru/match`, matchingData);
        
        console.log('マッチング結果:', response.data);
        
        if (response.data && Array.isArray(response.data)) {
          setMatchResults(response.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Matching error:', error);
        toast({
          title: 'エラーが発生しました',
          description: error.response?.data?.message || '商品の検索中にエラーが発生しました。もう一度お試しください。',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const renderStepContent = () => {
    if (matchResults) {
      return <MatchResults 
        results={matchResults} 
        onReset={handleReset}  // リセット関数を渡す
      />;
    }
  
    switch (activeStep) {
      case 0:
        return <GenderStep formData={formData} onChange={handleInputChange} />;
      case 1:
        return <BudgetStep formData={formData} onChange={handleInputChange} />;
      case 2:
        return <ColorStep formData={formData} onChange={handleInputChange} />;
      case 3:
        return <FeaturesStep formData={formData} onChange={handleInputChange} />;
      default:
        return null;
    }
  };

  return (
    <Box bgGradient="linear(to-b, blue.50, pink.50)" minH="100vh">
      <Container 
        maxW="container.xl" 
        py={{ base: 6, md: 10 }}   // 余白を画面サイズに応じて調整
        px={{ base: 4, md: 6 }}    // 横幅の余白も調整
      >
        <VStack spacing={{ base: 6, md: 8 }} align="stretch">
          {/* ヘッダー */}
          <Box textAlign="center">
            <Heading
              as="h1"
              size={{ base: "lg", md: "xl" }}  // モバイルでは小さく
              mb={{ base: 3, md: 4 }}
              bgGradient="linear(to-r, blue.600, pink.600)"
              bgClip="text"
            >
              ランドセルマッチング
            </Heading>
            {!matchResults && (
              <Text 
                fontSize={{ base: "md", md: "lg" }}  // モバイルでは小さく
                color="gray.600"
              >
                4つのステップで、お子様に最適なランドセルを見つけるお手伝いをします
              </Text>
            )}
          </Box>
  
          {matchResults ? (
            <Box>
              {renderStepContent()}
            </Box>
          ) : (
            <Box
              bg="white"
              borderRadius="2xl"
              boxShadow="xl"
              overflow="hidden"
            >
              {/* ステップインジケーター */}
              <Box 
                bg="white" 
                p={{ base: 4, md: 6 }} 
                borderBottomWidth="1px"
                overflowX="auto"  // モバイルでは横スクロール可能に
              >
                <Stepper 
                  index={activeStep}
                  size={{ base: "sm", md: "md" }}  // モバイルではコンパクトに
                >
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepIndicator>
                        <Icon as={step.icon} />
                      </StepIndicator>
                      <Box 
                        flexShrink='0'
                        display={{ base: 'none', md: 'block' }}  // モバイルでは説明を非表示
                      >
                        <StepTitle>{step.title}</StepTitle>
                        <StepDescription>{step.description}</StepDescription>
                      </Box>
                      <Box 
                        flexShrink='0'
                        display={{ base: 'block', md: 'none' }}  // モバイルではタイトルのみ表示
                      >
                        <StepTitle fontSize="sm">{step.title}</StepTitle>
                      </Box>
                      <StepSeparator />
                    </Step>
                  ))}
                </Stepper>
              </Box>
  
              {/* メインコンテンツ */}
              <Box 
                p={{ base: 4, md: 8 }}  // モバイルでは余白を小さく
                bg="gray.50"
              >
                {isLoading ? (
                  <Flex justify="center" align="center" minH="300px">
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                  </Flex>
                ) : (
                  renderStepContent()
                )}
              </Box>
  
              {/* ナビゲーションボタン */}
              <Flex 
                justify="space-between" 
                p={{ base: 4, md: 6 }} 
                bg="white" 
                borderTopWidth="1px"
                direction={{ base: "column", sm: "row" }}  // モバイルでは縦並び
                gap={{ base: 3, sm: 0 }}  // モバイルでボタン間の間隔を追加
              >
                <Button
                  onClick={handleBack}
                  isDisabled={activeStep === 0}
                  leftIcon={<ChevronLeft />}
                  variant="ghost"
                  color="gray.600"
                  w={{ base: "full", sm: "auto" }}  // モバイルでは幅いっぱい
                >
                  戻る
                </Button>
                <Button
                  onClick={handleNext}
                  rightIcon={<ChevronRight />}
                  colorScheme="blue"
                  isDisabled={!stepValidation[activeStep]}
                  w={{ base: "full", sm: "auto" }}  // モバイルでは幅いっぱい
                >
                  {activeStep === steps.length - 1 ? 'マッチング開始' : '次へ'}
                </Button>
              </Flex>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default InputForm;